import { useEffect, useRef, useState } from 'react';
import { initBuildMeta, subscribe, isStale, getLatestMeta } from '@/lib/buildMeta';

/**
 * Top-of-screen banner that appears the moment a NEWER build is detected on
 * the server (via /version.json polling). Distinct from VersionUpdateListener
 * which fires on the SW activate event — this one works even when the SW
 * isn't installed (e.g. private window, locked-down browsers, CDN caches
 * serving stale HTML to the SW).
 *
 * The banner is sticky-dismissible per build_id so users don't get nagged
 * if they choose "later" — but it re-appears for the NEXT build.
 */
export const DeploymentFreshnessBanner = () => {
  const [show, setShow] = useState(false);
  const [latestId, setLatestId] = useState(null);
  const dismissedFor = useRef(null);

  useEffect(() => {
    initBuildMeta();
    const unsub = subscribe(() => {
      const stale = isStale();
      const latest = getLatestMeta();
      const id = latest ? latest.build_id : null;
      setLatestId(id);
      if (!stale) { setShow(false); return; }
      if (dismissedFor.current === id) return; // user dismissed for this build
      setShow(true);
    });
    return unsub;
  }, []);

  const reload = () => {
    try {
      // Force SW + Cache Storage flush before reload — same logic as
      // /cache-reset.html, inlined for one-click UX.
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then((regs) =>
          Promise.all(regs.map((r) => r.unregister().catch(() => {})))
        ).finally(() => {
          if ('caches' in window) {
            caches.keys().then((keys) =>
              Promise.all(keys.map((k) => caches.delete(k).catch(() => {})))
            ).finally(() => window.location.reload());
          } else {
            window.location.reload();
          }
        });
      } else {
        window.location.reload();
      }
    } catch (_) {
      window.location.reload();
    }
  };

  const dismiss = () => {
    dismissedFor.current = latestId;
    setShow(false);
  };

  if (!show) return null;
  return (
    <div
      data-testid="deployment-freshness-banner"
      role="status"
      className="fixed top-0 inset-x-0 z-[60] flex items-center justify-center gap-3 bg-orange-600 text-white text-sm py-2 px-4 shadow-lg"
    >
      <span className="font-medium">A newer version of Smarto is available.</span>
      <button
        type="button"
        onClick={reload}
        data-testid="deployment-freshness-reload-btn"
        className="rounded-md bg-white text-orange-700 font-semibold px-3 py-1 text-xs hover:bg-orange-50 transition"
      >
        Refresh now
      </button>
      <button
        type="button"
        onClick={dismiss}
        data-testid="deployment-freshness-dismiss-btn"
        className="text-xs underline opacity-90 hover:opacity-100"
      >
        Later
      </button>
    </div>
  );
};

export default DeploymentFreshnessBanner;
